import { ApolloQueryResult } from '@apollo/client';
import type { ContentValue } from '@ds/data-table';
import { useLanguageCtx } from '@modules/language';
import { useGetHierarchyLazyQuery } from '@modules/search';
import {
    BreadcrumbNode,
    ContentType,
    HierarchyNode,
    NodeContentO,
    NodeContentValue,
    SearchFilter,
    SearchFilterOperator,
    SelectOptionValues,
} from '@src/__generated__/types';
import { FormValues } from '@src/assets/types';
import { InitialValue, MappedWatchedValues } from '@src/assets/types/hierarchies/Hierarchies.types';
import { isDefined } from '@src/components/hierarchies/helpers/isDefined';
import { GetNodeQuery, useGetNodeLazyQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { NodeData } from '@src/components/hierarchies/hooks/useTransformEditNodeData';
import { useEditNodeRoute } from '@src/components/hierarchies/shared/EditNodeRouteContext';
import { RemovedBaseMachineItem } from '@src/components/hierarchies/shared/ValidateHelpers';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { SelectedNode } from '@src/modules/hierarchy/types';
import { getValue } from '@src/modules/prototype/helpers/dataHelpers';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { EditNodeDialogContext } from '@src/store';
import { EditNodeContext } from '@src/store/hierarchy/EditNodeContext';
import { safeBase64Encode } from '@src/utils';
import {
    useCheckGlItemsLazyQuery,
    useCheckInvalidGlsLazyQuery,
} from '@src/utils/cgt/cgtQuery.generated';
import { get, isEqual, set } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormState, UseFormWatch } from 'react-hook-form';

import { useAuth } from '../auth';

const watchedFieldsAreDirty = (
    mappedWatchedValues: MappedWatchedValues,
    dirtyFields: Partial<
        Readonly<{
            [x: string]: any;
        }>
    >,
) =>
    Object.entries(dirtyFields).some(([key, value]) =>
        Object.keys(value[0]).some(key2 =>
            mappedWatchedValues.find(val => val.path === `${key}-${key2}`),
        ),
    );

const getFieldNameData = (name: string) => {
    const [ctg, rest] = name.split('.0.') ?? [];
    const [ct] = rest?.split('.') ?? [];

    return { ctg, ct };
};

// Models created in the usato/competitor hierarchy are always machines: model_type is
// prefilled with 'M'. The field is shown as read-only at creation by the schema's
// autoFilled flag, not by this code (the content type code is also hardcoded in the
// server-side usato/concorrenza handlers).
export const COMPETITOR_MODEL_TYPE_CONTENT_TYPE = 'model_type';
export const COMPETITOR_MODEL_TYPE_MACHINE = 'M';

// A backoffice node only links COMPATTE content: the commercial families under the COMPATTE
// gamma (the gamma's grandchildren, families being the 3rd level of the commercial family
// hierarchy: root > gamma > listino > famiglia) and the models / prototypes whose SAP product
// hierarchy segmento is CP.
export const BACKOFFICE_COMMERCIAL_FAMILY_GAMMA_CODE = 'COMPATTE';
export const BACKOFFICE_COMMERCIAL_FAMILY_DEPTH = '3,3';
export const BACKOFFICE_SAP_SEGMENTO = 'CP';
// Virtual search filter resolved by the server-side CGT getNodes override, which reads the
// segmento from the SAP product hierarchy node linked by the (prototype's) model.
export const SAP_SEGMENTO_FILTER_FIELD = 'sapSegmento';

export function useCgtLinkFieldHelpers(
    linkHierarchy: string | null | undefined,
    name: string,
    formState: FormState<FormValues>,
    watch: UseFormWatch<FormValues>,
    showOnlySelected: boolean = false,
) {
    const { cgtSettings } = useContext(SettingsContext);
    const { state: editNodeState } = useContext(EditNodeContext);
    const { state: editNodeDialogState } = useContext(EditNodeDialogContext);
    const [currFilter, setCurrFilter] = useState();
    const { sessionData } = useAuth();

    const isMMT = sessionData?.attributes?.company === 'cgt';
    const isEN = sessionData?.attributes?.company === 'cgt-en';

    const watchedValues = watch();

    const primaryPriceList = useMemo(() => {
        if (
            cgtSettings?.paths?.primaryPriceListValue &&
            ((cgtSettings.paths.accessories && name.startsWith(cgtSettings.paths.accessories)) || // MMT
                (cgtSettings.paths.baseMachine && name.startsWith(cgtSettings.paths.baseMachine)) || // MMT
                name.startsWith('logistic_groups.0.gls')) // EN
        ) {
            return get(watchedValues, cgtSettings?.paths?.primaryPriceListValue, []);
        }
        return undefined;
    }, [get(watchedValues, cgtSettings?.paths?.primaryPriceListValue ?? '')]);

    const secondaryPriceLists = useMemo(() => {
        if (
            cgtSettings?.paths?.secondaryPriceListValue &&
            ((cgtSettings.paths.accessories && name.startsWith(cgtSettings.paths.accessories)) || // MMT
                (cgtSettings.paths.baseMachine && name.startsWith(cgtSettings.paths.baseMachine)) || // MMT
                name.startsWith('logistic_groups.0.gls')) // EN
        ) {
            return get(watchedValues, cgtSettings?.paths?.secondaryPriceListValue, []);
        }
        return undefined;
    }, [get(watchedValues, cgtSettings?.paths?.secondaryPriceListValue ?? '')]);

    const [fetchNode, data] = useGetNodeLazyQuery();

    useEffect(() => {
        if (
            editNodeDialogState?.sapProductHierarchyId &&
            cgtSettings?.hierarchies.sapProductHierarchyId &&
            name === cgtSettings.paths.sapProductHierarchyValue
        ) {
            fetchNode({
                variables: {
                    hierarchiesId: cgtSettings.hierarchies.sapProductHierarchyId,
                    nodeFilter: { ids: [editNodeDialogState.sapProductHierarchyId] },
                },
            });
        }
    }, [editNodeDialogState, name]);

    const { ctg, ct } = getFieldNameData(name);

    const isPriceListSupplierModel = ctg === 'base_info' && ct === 'price_list';
    const isPriceListItem = ctg === 'items' && ct === 'items';

    const isGlLinkInCgtModel =
        editNodeState?.hierarchyId === cgtSettings?.hierarchies?.cGTModelHierarchyId &&
        linkHierarchy === cgtSettings?.hierarchies?.glHierarchyId;

    const isPriceListLinkInGl =
        isPriceListSupplierModel &&
        (editNodeDialogState?.hierarchyId ?? editNodeState?.hierarchyId) ===
            cgtSettings?.hierarchies?.glHierarchyId &&
        linkHierarchy === cgtSettings?.hierarchies?.priceListHierarchyId &&
        name === cgtSettings?.paths?.priceListValue;

    const isItemLinkInGl =
        isPriceListItem &&
        (editNodeDialogState?.hierarchyId ?? editNodeState?.hierarchyId) ===
            cgtSettings?.hierarchies?.glHierarchyId &&
        linkHierarchy === cgtSettings?.hierarchies?.priceListHierarchyId;

    const isBackofficeNode =
        !!cgtSettings?.hierarchies?.backofficeHierarchyId &&
        (editNodeDialogState?.hierarchyId ?? editNodeState?.hierarchyId) ===
            cgtSettings.hierarchies.backofficeHierarchyId;

    function extractPriceListToUse(
        extraInfo: Record<string, string | Record<string, string>> | undefined,
    ) {
        if (extraInfo?.priceListToUse) {
            return Object.values(extraInfo.priceListToUse)?.filter(isDefined) ?? [];
        }
        return undefined;
    }

    function getPriceListValues() {
        if (isMMT || isEN) {
            return [...primaryPriceList, ...secondaryPriceLists];
        }
        return undefined;
    }

    function getCgtExtraPayload(extraParams?: {
        extraInfo?: Record<string, string | Record<string, string>>[];
        nodeId: string;
    }) {
        const priceListToUse = extractPriceListToUse(
            extraParams?.extraInfo?.find(i => i?.id === extraParams.nodeId),
        );

        return {
            priceListValues: isGlLinkInCgtModel ? getPriceListValues() : undefined,
            secondaryPriceListValues: secondaryPriceLists,
            startingHierarchyId: editNodeState?.hierarchyId,
            startingNodeId: editNodeState?.nodeId,
            sapProductHierarchyId: get(
                formState.defaultValues,
                `${cgtSettings?.paths.sapProductHierarchyValue}.0`,
            ),
            priceListToUse,
        };
    }

    const staticOptionsMap = useMemo(
        () =>
            isGlLinkInCgtModel
                ? {
                      price_list: [
                          ...new Set([
                              ...(primaryPriceList?.filter(Boolean) ?? []),
                              ...(secondaryPriceLists?.filter(Boolean) ?? []),
                          ]),
                      ].map(o => ({ label: o, value: o })),
                  }
                : undefined,
        [primaryPriceList, secondaryPriceLists, isGlLinkInCgtModel],
    );

    const glPriceLists = useMemo(() => {
        if (isItemLinkInGl && cgtSettings?.paths?.priceListValue) {
            return get(watchedValues, cgtSettings?.paths?.priceListValue);
        }
        return null;
    }, [isItemLinkInGl, watchedValues]);

    const cgtExtraFilters = useMemo(() => {
        const extraFilters: SearchFilter[] = [];

        // sap product hierarchy filters in gl create/edit: they narrow the selectable sap
        // nodes to the ones compatible with the model's sap node. They must not apply while
        // "show only selected" is on, otherwise an already saved sap node that falls outside
        // them (e.g. the model's own node, copied on base machine creation) is hidden and the
        // grid looks empty even though the link is set.
        if (
            !showOnlySelected &&
            editNodeDialogState?.sapProductHierarchyId &&
            cgtSettings?.hierarchies.sapProductHierarchyId &&
            name === cgtSettings.paths.sapProductHierarchyValue
        ) {
            const sapProductHierarchyNode = data.data?.hierarchies?.[0]?.nodes?.[0];
            if (sapProductHierarchyNode) {
                const isBaseMachine = editNodeDialogState.linkFieldId?.includes('base_machine');

                const segmento = getValue(
                    sapProductHierarchyNode,
                    cgtSettings.fields.segmento.contentTypeCode,
                    false,
                );

                const marca = getValue(
                    sapProductHierarchyNode,
                    cgtSettings.fields.marca.contentTypeCode,
                    false,
                );

                const divisione = getValue(
                    sapProductHierarchyNode,
                    cgtSettings.fields.divisione.contentTypeCode,
                    false,
                );

                if (segmento && marca) {
                    extraFilters.push({
                        field: `base_info-${cgtSettings.fields.segmento.contentTypeCode}`,
                        operator: SearchFilterOperator.Includes,
                        value: [segmento],
                    });
                    extraFilters.push({
                        field: `base_info-${cgtSettings.fields.marca.contentTypeCode}`,
                        operator: SearchFilterOperator.Includes,
                        value: [marca],
                    });
                    extraFilters.push({
                        field: `base_info-${cgtSettings.fields.tipo.contentTypeCode}`,
                        operator: SearchFilterOperator.Includes,
                        value: [isBaseMachine ? 'M' : 'A'],
                    });
                }

                if (divisione) {
                    extraFilters.push({
                        field: `base_info-${cgtSettings.fields.divisione.contentTypeCode}`,
                        operator: SearchFilterOperator.Includes,
                        value: [divisione],
                    });
                }
            }
        }

        // depth filters when showing price lists (price lists are only 3rd depth level nodes)
        if (
            [
                cgtSettings?.paths.priceListValue,
                cgtSettings?.paths.primaryPriceListValue,
                cgtSettings?.paths.secondaryPriceListValue,
            ].includes(name)
        ) {
            extraFilters.push({
                field: 'depth',
                operator: SearchFilterOperator.Equals,
                value: ['3,3'],
            });
        }

        // in gl price list, show only price list from primary and secondary model price lists
        if (
            isPriceListLinkInGl &&
            (!!editNodeDialogState?.secondaryPriceListValues?.length ||
                !!editNodeDialogState?.priceListValues?.length)
        ) {
            const filterValues = [
                ...(editNodeDialogState.priceListValues ?? []),
                ...(editNodeDialogState?.secondaryPriceListValues ?? []),
            ];
            extraFilters.push({
                field: 'id',
                operator: SearchFilterOperator.IsAnyOf,
                value: filterValues,
            });
        }

        // filters current gl items based on model's primary price list value
        if (isItemLinkInGl && name === cgtSettings?.paths.itemsValue && glPriceLists) {
            extraFilters.push({
                field: 'parent',
                operator: SearchFilterOperator.IsAnyOf,
                value: glPriceLists,
            });

            // items's are 4th depth level nodes
            extraFilters.push({
                field: 'depth',
                operator: SearchFilterOperator.Equals,
                value: ['4,4'],
            });
        }

        if (
            isGlLinkInCgtModel &&
            editNodeDialogState?.hierarchyId === cgtSettings?.hierarchies.cGTModelHierarchyId
        ) {
            extraFilters.push({
                field: 'base_info-price_list',
                operator: SearchFilterOperator.IsAnyOf,
                value: [
                    ...(primaryPriceList?.filter(Boolean) ?? []),
                    ...(secondaryPriceLists?.filter(Boolean) ?? []),
                ],
            });
        }

        // backoffice links are restricted to COMPATTE content. Not while "show only selected" is
        // on, so an already saved link outside the restriction is still listed.
        if (isBackofficeNode && !showOnlySelected && cgtSettings) {
            if (linkHierarchy === cgtSettings.hierarchies.commercialFamilyHierarchyId) {
                extraFilters.push({
                    field: 'ancestor',
                    operator: SearchFilterOperator.Equals,
                    value: [BACKOFFICE_COMMERCIAL_FAMILY_GAMMA_CODE],
                });
                extraFilters.push({
                    field: 'depth',
                    operator: SearchFilterOperator.Equals,
                    value: [BACKOFFICE_COMMERCIAL_FAMILY_DEPTH],
                });
            }

            if (
                linkHierarchy === cgtSettings.hierarchies.cGTModelHierarchyId ||
                linkHierarchy === cgtSettings.prototypeSetting.prototypeHierarchyId
            ) {
                extraFilters.push({
                    field: SAP_SEGMENTO_FILTER_FIELD,
                    operator: SearchFilterOperator.Equals,
                    value: [BACKOFFICE_SAP_SEGMENTO],
                });
            }
        }

        return extraFilters;
    }, [
        editNodeDialogState,
        name,
        isItemLinkInGl,
        isPriceListLinkInGl,
        cgtSettings,
        data,
        isGlLinkInCgtModel,
        primaryPriceList,
        secondaryPriceLists,
        glPriceLists,
        showOnlySelected,
        isBackofficeNode,
        linkHierarchy,
    ]);

    const getCgtDynamicValues = useCallback(
        (mappedWatchedValues: MappedWatchedValues) => {
            if (
                !cgtSettings ||
                name !== cgtSettings?.paths?.itemsValue ||
                !editNodeDialogState ||
                editNodeDialogState.hierarchyId !== cgtSettings?.hierarchies?.glHierarchyId ||
                !editNodeDialogState.linkFieldId?.includes(cgtSettings.prototypeSetting.glCodes[0])
            )
                return null;
            if (!!mappedWatchedValues?.length && !!editNodeState?.initialValues) {
                const values = watchedFieldsAreDirty(mappedWatchedValues, formState?.dirtyFields)
                    ? watch()
                    : editNodeState.initialValues;
                const reduced = mappedWatchedValues.reduce((acc, val) => {
                    const value = get(values, val.valueContext)?.[0];
                    if (isEqual(currFilter, value) || !currFilter) {
                        setCurrFilter(value);
                        return acc;
                    }
                    setCurrFilter(value);
                    set(acc, val.valueContext, value);
                    return acc;
                }, {});
                if (Object.keys(reduced).length) return reduced;
            }
            return null;
        },
        [currFilter, formState, editNodeState, editNodeDialogState, cgtSettings],
    );

    const showViewNode = true;

    return {
        showViewNode,
        cgtExtraFilters,
        getCgtDynamicValues,
        getCgtExtraPayload,
        isGlLinkInCgtModel,
        isItemLinkInGl,
        isPriceListItem,
        isPriceListSupplierModel,
        staticOptionsMap,
    };
}

export type DependantGlsResponse = {
    gls: {
        id: string;
        glCode: string;
        priceListCodes: string[];
    }[];
    priceLists: string[];
};

export type BaseMachineItemsRemoval = {
    items: RemovedBaseMachineItem[];
    // snapshot of the aborted save, replayed as is when the removal is confirmed: it carries
    // every pending change, so no field has to be re-marked dirty by hand
    formValues: InitialValue;
    formDirtyFields: InitialValue;
};

export function useCgtEditNodeHelpers(isCreate?: boolean) {
    const { hasUserRole, sessionData } = useAuth();
    const { cgtSettings } = useContext(SettingsContext);
    const [invalidGls, setInvalidGls] = useState<({ id: string; code: string } | null)[] | null>(
        null,
    );
    const [dependantResponse, setDependantResponse] = useState<DependantGlsResponse | null>();
    const [baseMachineItemsRemoval, setBaseMachineItemsRemoval] =
        useState<BaseMachineItemsRemoval | null>(null);

    const [fetchNodesWithContents] = useGetHierarchyLazyQuery();

    // Read through the EditNode route context so the form is not re-rendered by navigations.
    const { fromPrototypeGlId, navigate } = useEditNodeRoute();

    const { state: editNodeDialogState } = useContext(EditNodeDialogContext);
    const { defaultLanguage } = useLanguageCtx();

    const [checkGLItems, { loading: checkGlItemsLoading }] = useCheckGlItemsLazyQuery({
        fetchPolicy: 'network-only',
    });

    const [checkInvalidGls, { loading: checkInvalidGlsLoading }] = useCheckInvalidGlsLazyQuery({
        fetchPolicy: 'network-only',
    });

    const { cGTModelHierarchyId, cGTModelUsatoCompetitorHierarchyId } =
        cgtSettings?.hierarchies ?? {};

    const { company } = sessionData?.attributes ?? {};

    const shouldInitCGTValue = (hierarchyId: string) =>
        (editNodeDialogState?.startingHierarchyId ===
            cgtSettings?.hierarchies?.cGTModelHierarchyId &&
            hierarchyId === cgtSettings?.hierarchies?.glHierarchyId) ||
        editNodeDialogState?.hierarchyId ===
            cgtSettings?.hierarchies.cGTModelUsatoCompetitorHierarchyId;

    function initCGTDefaultValue(contentTypeCode: string) {
        // init node type field
        if (contentTypeCode === cgtSettings?.fields?.nodeTypeField.contentTypeCode)
            return [
                {
                    value: [cgtSettings?.fields?.nodeTypeField.defaultValue],
                    languageIsoCode: defaultLanguage,
                },
            ] as NodeContentValue[];

        // init model type field: competitor models are always machines (creation only,
        // existing nodes keep their stored value)
        if (
            isCreate &&
            !!cgtSettings &&
            editNodeDialogState?.hierarchyId ===
                cgtSettings.hierarchies.cGTModelUsatoCompetitorHierarchyId &&
            contentTypeCode === COMPETITOR_MODEL_TYPE_CONTENT_TYPE
        )
            return [
                {
                    value: [COMPETITOR_MODEL_TYPE_MACHINE],
                    languageIsoCode: defaultLanguage,
                },
            ] as NodeContentValue[];

        // init type field
        if (
            cgtSettings?.paths?.baseMachine &&
            editNodeDialogState?.linkFieldId?.startsWith(cgtSettings?.paths.baseMachine) &&
            contentTypeCode === cgtSettings?.fields?.typeField.contentTypeCode
        )
            return [
                {
                    value: [cgtSettings?.fields?.typeField.defaultValue],
                    languageIsoCode: defaultLanguage,
                },
            ] as NodeContentValue[];

        if (
            !isCreate &&
            ((cgtSettings?.paths?.baseMachine &&
                editNodeDialogState?.linkFieldId?.startsWith(cgtSettings?.paths.baseMachine)) || // MMT
                (cgtSettings?.paths?.accessories &&
                    editNodeDialogState?.linkFieldId?.startsWith(cgtSettings?.paths.accessories)) || // MMT
                editNodeDialogState?.linkFieldId?.startsWith('logistic_groups.0.gls')) && // EN
            contentTypeCode === cgtSettings?.fields?.priceListField.contentTypeCode &&
            !!editNodeDialogState?.priceListValues?.length
        ) {
            const priceLists = editNodeDialogState.priceListToUse?.flatMap(p => p?.split('$$'));
            return [{ value: priceLists, languageIsoCode: defaultLanguage }] as NodeContentValue[];
        }

        return undefined;
    }

    const filterCGTOptions = (
        data: GetNodeQuery,
        content: ContentType,
        contentValue?: ContentType & { value?: ContentValue['content']['value'] },
    ): SelectOptionValues[] | null | undefined => {
        const isCertified =
            getValue(data.hierarchies?.[0].nodes[0], 'certification_status') ===
            cgtSettings?.prototypeSetting.certificationStatuses.certified;

        // remove accessories options if is a base machine gl
        if (
            content.code === cgtSettings?.fields?.typeField.contentTypeCode &&
            cgtSettings?.paths?.baseMachine &&
            editNodeDialogState?.linkFieldId?.startsWith(cgtSettings?.paths.baseMachine)
        ) {
            return content.optionalValues?.filter(
                o => o.code === cgtSettings?.fields?.typeField.defaultValue,
            );
        }

        // remove base machine option if is an accessory gl
        if (
            content.code === cgtSettings?.fields?.typeField.contentTypeCode &&
            cgtSettings?.paths?.accessories &&
            editNodeDialogState?.linkFieldId?.startsWith(cgtSettings?.paths.accessories)
        ) {
            if (isCertified) {
                return content.optionalValues?.filter(
                    o => o.code === contentValue?.value?.[0].value?.[0],
                );
            }

            return content.optionalValues?.filter(
                o => o.code !== cgtSettings?.fields?.typeField.defaultValue,
            );
        }

        return content.optionalValues;
    };

    const getShowCode = (createNode: boolean, hierarchyId: string) => {
        const isMMT = sessionData?.attributes?.company === 'cgt';
        return (
            createNode &&
            ![
                cgtSettings?.hierarchies?.cGTModelHierarchyId,
                cgtSettings?.hierarchies?.glHierarchyId,
                cgtSettings?.hierarchies?.glGenericHierarchyId,
                cgtSettings?.hierarchies?.attrezzatureFuoriListinoHierarchyId,
                cgtSettings?.hierarchies?.priceListHierarchyId,
                cgtSettings?.hierarchies?.cGTModelUsatoCompetitorHierarchyId,
                cgtSettings?.hierarchies?.sezioniSchedeHierarchyId,
            ]
                .concat(isMMT ? [cgtSettings?.hierarchies?.commercialFamilyHierarchyId] : [])
                .includes(hierarchyId)
        );
    };

    const showExportLongLists = (
        nodeId: string,
        hierarchyId: string,
        breadcrumb: BreadcrumbNode[] | undefined,
        selectedNode: SelectedNode | undefined,
        nodeData: NodeData,
    ) =>
        hierarchyId === cgtSettings?.hierarchies?.commercialFamilyHierarchyId &&
        ((breadcrumb?.length === 4 && selectedNode?.id === nodeId) ||
            (breadcrumb?.length === 3 && selectedNode?.id === nodeData?.parent));

    const showLongListsOrderer = (
        nodeId: string,
        hierarchyId: string,
        breadcrumb: BreadcrumbNode[] | undefined,
        selectedNode: SelectedNode | undefined,
        nodeData: NodeData,
    ) =>
        hierarchyId === cgtSettings?.hierarchies?.commercialFamilyHierarchyId &&
        ((breadcrumb?.length === 4 && selectedNode?.id === nodeId) ||
            (breadcrumb?.length === 3 && selectedNode?.id === nodeData?.parent));

    const showModelActions = (
        hierarchyId: string,
        breadcrumb: BreadcrumbNode[] | undefined,
        nodeData: NodeData,
    ) =>
        sessionData?.attributes?.company === 'cgt' &&
        hierarchyId === cgtSettings?.hierarchies?.cGTModelHierarchyId &&
        (breadcrumb?.length === 2 ||
            (breadcrumb?.length === 1 && !breadcrumb?.find(b => b.id === nodeData?.id)));

    const showENModelCreateGl = (hierarchyId: string) =>
        sessionData?.attributes?.company === 'cgt-en' &&
        hierarchyId === cgtSettings?.hierarchies.cGTModelHierarchyId;

    const showModelStatuses = (hierarchyId: string) =>
        hierarchyId === cgtSettings?.hierarchies?.cGTModelHierarchyId &&
        cgtSettings.showNodeStatuses;

    const showCertificate = (hierarchyId: string, nodeId: string) => {
        if (hierarchyId === nodeId) return false; // never show certificate action on hierarchy root node

        const hasPublishPermission =
            (company === 'cgt' &&
                hasUserRole('CGT_PUBLISH_SAP') &&
                hierarchyId === cGTModelUsatoCompetitorHierarchyId) ||
            (company === 'cgt-en' &&
                hasUserRole('CGT_EN_PUBLISH_SAP') &&
                (hierarchyId === cGTModelUsatoCompetitorHierarchyId ||
                    hierarchyId === cGTModelHierarchyId));

        if (hierarchyId === cgtSettings?.hierarchies?.cGTModelHierarchyId && company === 'cgt-en') {
            return hasPublishPermission;
        }

        return (
            hierarchyId === cgtSettings?.hierarchies?.cGTModelUsatoCompetitorHierarchyId &&
            hasPublishPermission
        );
    };

    const isCommercialFamilyHierarchy = (hierarchyId: string) =>
        hierarchyId === cgtSettings?.hierarchies?.commercialFamilyHierarchyId;

    const showPublishFamily = (
        nodeId: string,
        hierarchyId: string,
        breadcrumb: BreadcrumbNode[] | undefined,
        selectedNode: SelectedNode | undefined,
        nodeData: NodeData,
    ) =>
        hierarchyId === cgtSettings?.hierarchies?.commercialFamilyHierarchyId &&
        ((breadcrumb?.length === 3 && selectedNode?.id === nodeId) ||
            (breadcrumb?.length === 2 && selectedNode?.id === nodeData?.parent));

    const showReplaceItem = (
        hierarchyId: string,
        selectedNode: HierarchyNode | undefined,
        breadcrumb: BreadcrumbNode[] | undefined,
    ) =>
        hierarchyId === cgtSettings?.hierarchies?.priceListHierarchyId &&
        ((breadcrumb?.length === 4 && !breadcrumb.find(n => n.id === selectedNode?.id)) ||
            (breadcrumb?.length ?? 0) > 4);

    const showChangeParent = (isInTranslation: boolean) => !isInTranslation;

    const getChangeParentDisabled = (hierarchyId: string) =>
        cgtSettings?.disableChangeParentHierarchy?.includes(hierarchyId) ?? false;

    const showPrototypes = (hierarchyId: string) =>
        hierarchyId === cgtSettings?.hierarchies.cGTModelHierarchyId;

    const getIsModelloConcorrenza = (hierarchyId: string) =>
        hierarchyId === cgtSettings?.hierarchies?.cGTModelUsatoCompetitorHierarchyId;

    const isCommecialFamilyYearLevel = (
        hierarchyId: string,
        breadcrumb: BreadcrumbNode[] | undefined,
    ) =>
        hierarchyId === cgtSettings?.hierarchies?.commercialFamilyHierarchyId &&
        breadcrumb?.length === 3;

    const showDisableNode = (hierarchyId: string, nodeData: NodeData) =>
        !cgtSettings || hierarchyId !== nodeData?.id;

    const updateModelForm = (
        values: InitialValue,
        dirtyFields: InitialValue,
        nodeContents: NodeContentO[],
    ) => {
        const updatedSecondaryPriceLists = get(
            values,
            'base_info.0.secondary_price_list.value.0.value',
        );

        const savedSecondaryPriceLists =
            nodeContents.find(c => c.contentType === 'secondary_price_list')?.value?.[0]?.value ??
            [];

        const secondaryPriceLists = updatedSecondaryPriceLists.length
            ? updatedSecondaryPriceLists
            : savedSecondaryPriceLists;

        const [primaryPriceList] = get(values, 'base_info.0.primary_price_list.value.0.value');

        if (primaryPriceList && secondaryPriceLists.includes(primaryPriceList)) {
            set(values, 'base_info.0.secondary_price_list.value.0.value', [
                ...(secondaryPriceLists?.filter(p => p !== primaryPriceList) ?? []),
            ]);
            set(dirtyFields, 'base_info.0.secondary_price_list.value.0.value', true);
        }
    };

    const customNavigate = (modelId: string, prototypeId: string, longListId: string) => {
        const prototypesQuery = `prototypes=${safeBase64Encode(prototypeId)}`;
        const longlistQuery = `scheda=${safeBase64Encode(longListId)},${safeBase64Encode(
            prototypeId,
        )}`;
        const longlistUrl = `/prototypes/${safeBase64Encode(
            modelId,
        )}?${prototypesQuery}&${longlistQuery}`;
        window.open(longlistUrl);
    };

    const hasCustomNavigation = (hierarchyId: string, linkedHierarchyId: string) =>
        linkedHierarchyId === cgtSettings?.hierarchies.longListsHierarchyId &&
        [
            cgtSettings?.prototypeSetting.prototypeHierarchyId,
            cgtSettings?.hierarchies.commercialFamilyHierarchyId,
        ].includes(hierarchyId);

    const customCommercialFamilyNavigation = (
        nodeData: NodeData,
        hierarchyId: string,
        nodeId: string,
    ) => {
        if (hasCustomNavigation(nodeData?.hierarchyId ?? '', hierarchyId)) {
            if (nodeData?.hierarchyId === cgtSettings?.prototypeSetting.prototypeHierarchyId) {
                const modelId = getValue(nodeData, 'linked_cgt_model');
                if (!modelId) {
                    SnackBarUtils.error(`Modello linkato alla scheda mancante`);
                    return;
                }
                if (!nodeData?.id) {
                    SnackBarUtils.error(`Prototipo linkato alla scheda mancante`);
                    return;
                }
                customNavigate(modelId, nodeData.id, nodeId);
            } else if (
                nodeData?.hierarchyId === cgtSettings?.hierarchies.commercialFamilyHierarchyId
            ) {
                const modelId = nodeData?.linkedBy
                    ?.find(l => l?.nodeId === nodeId)
                    ?.linkedNodes?.find(
                        l => l?.hierarchyId === cgtSettings?.hierarchies.cGTModelHierarchyId,
                    )?.nodeId;
                if (!modelId) {
                    SnackBarUtils.error(`Modello linkato alla scheda mancante`);
                    return;
                }
                const prototypeId = nodeData?.linkedBy
                    ?.find(l => l?.nodeId === nodeId)
                    ?.linkedNodes?.find(
                        l => l?.hierarchyId === cgtSettings?.prototypeSetting.prototypeHierarchyId,
                    )?.nodeId;
                if (!prototypeId) {
                    SnackBarUtils.error(`Prototipo linkato alla scheda mancante`);
                    return;
                }
                customNavigate(modelId, prototypeId, nodeId);
            }
        }
    };

    const customGlNavigation = async (
        hierarchyId: string,
        node: string,
        modelId: string | undefined,
        refetchNode: () => Promise<ApolloQueryResult<GetNodeQuery>>,
        // The model link field the gl was created from. Carried over to the model page because
        // the model form cannot tell base_machine from accessories until the new link is
        // projected, and the wrong one changes the filters of the reopened dialog.
        linkFieldId?: string | null,
    ) => {
        if (
            isCreate &&
            hierarchyId === cgtSettings?.hierarchies.glHierarchyId &&
            !fromPrototypeGlId
        ) {
            await refetchNode();
            const h = btoa(encodeURIComponent(cgtSettings?.hierarchies.cGTModelHierarchyId ?? ''));
            const n = btoa(encodeURIComponent(modelId ?? ''));

            navigate(`/hierarchies/${h}/${n}`, {
                state: {
                    clickType: 'glCreation',
                    glId: node,
                    modelId,
                    firstStep: true,
                    linkFieldId,
                },
            });
        }
    };

    const shouldShowReplaceNodeInCGT = (hierarchyId: string) =>
        cgtSettings ? cgtSettings.enableReplaceLinkHierarchy?.includes(hierarchyId) : true;

    return {
        filterCGTOptions,
        shouldInitCGTValue,
        initCGTDefaultValue,
        getShowCode,
        showExportLongLists,
        showLongListsOrderer,
        showModelActions,
        showENModelCreateGl,
        showModelStatuses,
        isCommercialFamilyHierarchy,
        checkGLItems,
        checkGlItemsLoading,
        checkInvalidGls,
        checkInvalidGlsLoading,
        showCertificate,
        showReplaceItem,
        showChangeParent,
        getChangeParentDisabled,
        showPrototypes,
        glHierarchyId: cgtSettings?.hierarchies?.glHierarchyId,
        cgtModelHierarchyId: cgtSettings?.hierarchies.cGTModelHierarchyId,
        priceListHierarchyId: cgtSettings?.hierarchies.priceListHierarchyId,
        glItemsField: cgtSettings?.fields?.itemsField,
        getIsModelloConcorrenza,
        isCommecialFamilyYearLevel,
        invalidGls,
        setInvalidGls,
        dependantResponse,
        setDependantResponse,
        baseMachineItemsRemoval,
        setBaseMachineItemsRemoval,
        fetchNodesWithContents,
        updateModelForm,
        showDisableNode,
        hasCustomNavigation,
        customCommercialFamilyNavigation,
        customGlNavigation,
        showPublishFamily,
        shouldShowReplaceNodeInCGT,
    };
}

export const useCgtDuplicateHelpers = () => {
    const currentYear = new Date().getFullYear();

    const commercialFamilyYearsOptions = Array.from({ length: 10 }, (__, i) =>
        String(currentYear - 3 + i),
    );
    const italianYearMonths = Array.from({ length: 12 }, (__, i) => {
        const month = new Date(0, i).toLocaleString('it-IT', { month: 'long' });
        return `${month[0].toUpperCase()}${month.slice(1)}`;
    });

    return { currentYear, commercialFamilyYearsOptions, italianYearMonths };
};
